import { baseApi } from './baseApi';
import type { User, AgentRank } from '../../types/models';

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UsersListResponse {
  data: User[];
  pagination: PaginationMeta;
}

interface UsersQuery {
  page?: number;
  limit?: number;
  role?: string;
  office?: string;
  /** Inmobiliaria. Solo lo aplica el backend para roles de plataforma (soporte/root). */
  tenantId?: string;
  isActive?: 'true' | 'false';
  search?: string;
  /** Filtra por equipo interno (User.teamTags). */
  teamTag?: string;
  /** Filtra por clasificación interna A/B/C/D. El backend lo ignora si no sos líder. */
  grade?: 'A' | 'B' | 'C' | 'D';
}

interface CreateUserBody {
  email: string;
  password: string;
  name: string;
  lastName: string;
  phone?: string;
  role?: string;
  office?: string;
  biography?: string;
  country?: string;
  city?: string;
  /** Barrio donde vive — para repartir leads por cercanía. */
  neighborhood?: string;
  address?: string;
  /**
   * "Ya sé que hay otro con el mismo nombre, crealo igual."
   *
   * El alta devuelve 422 cuando ya existe un perfil con ese nombre y apellido en la
   * inmobiliaria. Reintentar con esto en true lo crea igual (homónimos reales).
   */
  confirmarDuplicado?: boolean;
}

interface UpdateUserBody {
  name?: string;
  lastName?: string;
  phone?: string;
  biography?: string;
  country?: string;
  /** Departamento — primer nivel del selector de zona. */
  state?: string;
  city?: string;
  /** Barrio donde vive — para repartir leads por cercanía. */
  neighborhood?: string;
  address?: string;
  avatar?: string;
  taxId?: string;
  /** Cumpleaños (YYYY-MM-DD). null lo borra. Del año no se muestra nada. */
  birthDate?: string | null;
  /** Fecha de ingreso / alta (YYYY-MM-DD) — la antigüedad. null la borra. */
  joinedAt?: string | null;
}

/** Nota de seguimiento que un líder escribe sobre un agente (privada de líderes). */
export interface AgentNote {
  _id: string;
  agent: string;
  content: string;
  /** `action` lleva fecha y se marca como hecha; `note` es el registro de lo que pasó. */
  kind: 'note' | 'action';
  dueAt?: string | null;
  done: boolean;
  doneAt?: string | null;
  createdBy: string;
  createdBySnapshot: { name: string; lastName: string; avatar?: string };
  createdAt: string;
  updatedAt: string;
}

/** Estado de WhatsApp con Gustavo, para el CTA de reabrir la ventana. */
export interface MyWhatsappStatus {
  gustavoNumber: string;
  myPhone: string | null;
  configured: boolean;
  /** false = Meta bloquea los mensajes libres; los avisos quedan solo en el CRM. */
  ventanaAbierta: boolean;
  /** Horas desde el último mensaje del agente. null = nunca le escribió. */
  horasSinEscribir: number | null;
  ultimoInbound: string | null;
}

/** Altas de agentes en una ventana de meses, con el detalle y el corte por oficina. */
export interface AltaAgente {
  userId: string; name: string; avatar: string | null; office: string; joinedAt: string;
}
export interface BajaAgente {
  userId: string;
  name:   string;
  avatar: string | null;
  office: string;
  leftAt: string;
}

export interface AltasStats {
  meses: 1 | 3 | 6;
  desde: string;
  /** Plantel activo completo. */
  total: number;
  totalNuevos: number;
  /** Agentes SIN fecha de alta cargada: no se pueden contar como alta. */
  sinFecha: number;
  nuevos: AltaAgente[];
  porOficina: Array<{ office: string; total: number; nuevos: number }>;
  /** Egresos del período. El campo `deactivatedAt` se empezó a registrar hace poco, así
   *  que el histórico viejo no está: `bajasSinFecha` dice cuántas quedaron afuera. */
  totalBajas: number;
  bajasSinFecha: number;
  bajas: BajaAgente[];
}

/** Quiénes están dejando de entrar al CRM (semáforo + los más fríos). */
export interface AgenteSinEntrar {
  userId: string; name: string; avatar: string | null; office: string;
  /** Días desde la última actividad; si nunca entró, desde el alta. */
  dias: number | null;
  ultimaEntrada: string | null;
  /** Todavía no entró nunca: los `dias` se cuentan desde que se lo dio de alta. */
  nuncaEntro?: boolean;
  /** Por dónde se lo vio la última vez. Hablarle a Gustavo por WhatsApp cuenta. */
  via?: 'crm' | 'whatsapp' | null;
}
export interface InactividadStats {
  total: number;
  /** Entró hace 4 días o menos. */
  alDia: number;
  /** Entre 5 días y 2 semanas. */
  tibios: number;
  /** Más de 2 semanas, o nunca. */
  frios: number;
  nunca: number;
  agentes: AgenteSinEntrar[];
  porOficina: Array<{ office: string; total: number; frios: number }>;
}

export interface ResultadoBaja {
  userId: string;
  isActive: boolean;
  propiedadesBajadas?: number;
  propiedades?: string[];
  /** Las que quedaron ACTIVAS y publicadas: hay que arreglarlas a mano. */
  fallidas?: Array<{ propertyId: string; motivo: string }>;
  captaciones?: { descartadas: number; avisosLiberados: number; deLaBolsa: number; propias: number };
}

export interface PreviewBaja {
  agente: string;
  seDanDeBaja: {
    propiedades: number;
    enWeb: number;
    enInfocasas: number;
    enMeli: number;
    detalle: Array<{ propertyId: string; title?: string; enMeli: boolean; enInfocasas: boolean }>;
  };
  captaciones: { total: number; vuelvenALaBolsa: number; seDesactivan: number };
  quedanEnPie: { clientes: number; visitasProximas: number };
  /**
   * Lo que el Agente Virtual le va a seguir mandando si nadie lo mueve.
   *
   * Faltaba antes de apretar "dar de baja": no alcanza con saber cuántas fichas se
   * despublican. Si la persona queda como asesor de propiedades conectadas al Agente, o
   * con consultas y visitas encima, todo eso se convierte en trabajo que nadie ve.
   * `responsable` es a quién se le traspasa al confirmar. (09/09/2026)
   */
  agenteVirtual: {
    propiedadesConectadas: number;
    consultasDerivadas: number;
    visitasAgendadas: number;
    responsable: { userId: string; name: string } | null;
  };
}

export interface MiPortfolio {
  agente: { nombre: string; apellido: string; desde?: number | null } & Record<string, unknown>;
  seo?: { title: string; description: string; image: string; usandoLogo: boolean; url: string | null };
  numeros: { cartera: number; cerradas: number | null; resenas: number; promedio: number | null };
  premios: Array<{ titulo: string; subtitulo: string | null; fecha: string }>;
  propiedades: unknown[];
  resenas: unknown[];
  edicion: {
    slug: string; url: string; published: boolean; publishedAt: string | null;
    headline: string; biography: string; zones: string[]; specialties: string[]; languages: string[];
    socials: { instagram?: string; facebook?: string; linkedin?: string; tiktok?: string; web?: string };
    showClosings: boolean;
    /** Lo que le falta para que la página no salga coja: "foto", "biografía". */
    faltan: string[];
  };
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    /** "Mi página": preview + lo editable. 404 si la flag no lo alcanza. */
    getMiPortfolio: builder.query<MiPortfolio, void>({
      query: () => '/users/me/portfolio',
      transformResponse: (res: { data: MiPortfolio }) => res.data,
      providesTags: ['MiPortfolio'],
    }),

    updateMiPortfolio: builder.mutation<MiPortfolio, Record<string, unknown>>({
      query: (body) => ({ url: '/users/me/portfolio', method: 'PATCH', body }),
      transformResponse: (res: { data: MiPortfolio }) => res.data,
      invalidatesTags: ['MiPortfolio'],
    }),

    getMyProfile: builder.query<User & { ranking?: { isActive: boolean; points: number; history: any[] }; address?: string; hasSeenShowcase?: boolean; hasCompletedOnboarding?: boolean; mustChangePassword?: boolean; avisosVistos?: string[] }, void>({
      query: () => '/users/me',
      transformResponse: (res: { data: any }) => res.data,
      providesTags: [{ type: 'User', id: 'ME' }],
    }),

    /** Marca al user como "ya vio el showcase de onboarding" (idempotente). */
    markShowcaseSeen: builder.mutation<{ hasSeenShowcase: boolean }, void>({
      query: () => ({ url: '/users/me/showcase-seen', method: 'POST' }),
      transformResponse: (res: { data: { hasSeenShowcase: boolean } }) => res.data,
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /**
     * Cierra un anuncio de una lámina (idempotente).
     *
     * NO invalida el perfil a propósito: el gate ya se cerró en pantalla y refrescar
     * `/me` acá solo provoca un re-render con el overlay saliendo. La lista al día llega
     * en el próximo `/me` natural.
     */
    marcarAvisoVisto: builder.mutation<{ avisosVistos: string[] }, string>({
      query: (clave) => ({ url: `/users/me/avisos/${encodeURIComponent(clave)}`, method: 'POST' }),
      transformResponse: (res: { data: { avisosVistos: string[] } }) => res.data,
    }),

    /** Marca al agente como "ya pasó el onboarding guiado de Gustavo" (idempotente). */
    markOnboardingComplete: builder.mutation<{ hasCompletedOnboarding: boolean }, void>({
      query: () => ({ url: '/users/me/onboarding/complete', method: 'POST' }),
      transformResponse: (res: { data: { hasCompletedOnboarding: boolean } }) => res.data,
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /** Opt-in/out del brief matutino de Gustavo por WhatsApp (preferencia propia). */
    setGustavoMorningBrief: builder.mutation<{ gustavoMorningBrief: boolean }, boolean>({
      query: (enabled) => ({ url: '/users/me/gustavo-brief', method: 'PATCH', body: { enabled } }),
      transformResponse: (res: { data: { gustavoMorningBrief: boolean } }) => res.data,
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /** Estado de WhatsApp con Gustavo: número de Gustavo + si el agente ya cargó el suyo. */
    getMyWhatsappStatus: builder.query<MyWhatsappStatus, void>({
      query: () => ({ url: '/users/me/whatsapp' }),
      transformResponse: (res: { data: MyWhatsappStatus }) => res.data,
      providesTags: [{ type: 'User', id: 'ME' }],
    }),

    /** Agentes que dejaron de entrar al CRM (widget al lado de Plantel). */
    getInactividadStats: builder.query<InactividadStats, { office?: string } | void>({
      query: (args) => ({ url: '/users/inactividad-stats', params: { ...(args?.office ? { office: args.office } : {}) } }),
      transformResponse: (res: { data: InactividadStats }) => res.data,
      providesTags: ['User'],
    }),

    /** Altas de agentes (widget de soporte y del admin del tenant). */
    getAltasStats: builder.query<AltasStats, { meses?: 1 | 3 | 6; office?: string } | void>({
      query: (args) => ({ url: '/users/altas-stats', params: { meses: args?.meses ?? 1, ...(args?.office ? { office: args.office } : {}) } }),
      transformResponse: (res: { data: AltasStats }) => res.data,
      providesTags: ['User'],
    }),

    listUsers: builder.query<UsersListResponse, UsersQuery>({
      query: (params) => ({ url: '/users', params }),
      transformResponse: (res: { data: User[]; pagination: PaginationMeta }) =>
        ({ data: res.data, pagination: res.pagination }),
      providesTags: ['User'],
    }),

    /**
     * Lista de agentes visibles para selectores (referido / conector / co-listing).
     * No requiere `users:read`; scope automático por oficina del user actual.
     */
    /**
     * Compañeros para asociar a una operación.
     * Sin argumentos → los de la oficina propia (co-listing, extras de comisión).
     * `{ scope: 'tenant' }` → todas las oficinas, solo para COMPARTIR LA RESERVA, que
     * es el único caso que la dirección habilitó a cruzar oficinas (01/08/2026).
     * Trae `officeName` para distinguir homónimos al mezclar oficinas.
     */
    listColleagues: builder.query<Array<User & { officeName?: string | null }>, { scope?: 'tenant' } | void>({
      query: (args) => ({ url: '/users/colleagues', params: args?.scope ? { scope: args.scope } : {} }),
      transformResponse: (res: { data: Array<User & { officeName?: string | null }> }) => res.data,
      providesTags: [{ type: 'User', id: 'COLLEAGUES' }],
    }),

    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      transformResponse: (res: { data: User }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),

    createUser: builder.mutation<User, CreateUserBody>({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: ['User'],
    }),

    updateUser: builder.mutation<User, { id: string; body: UpdateUserBody }>({
      query: ({ id, body }) => ({ url: `/users/${id}`, method: 'PUT', body }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, { type: 'User', id: 'ME' }, 'User'],
    }),

    uploadUserAvatar: builder.mutation<{ url: string; user: User }, { id: string; file: File }>({
      query: ({ id, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `/users/${id}/avatar`, method: 'POST', body: form };
      },
      transformResponse: (res: { data: { url: string; user: User } }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, { type: 'User', id: 'ME' }, 'User'],
    }),

    updateUserRole: builder.mutation<User, { id: string; role: string }>({
      query: ({ id, role }) => ({ url: `/users/${id}/role`, method: 'PUT', body: { role } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    /**
     * La oficina NO viaja en el update general: `UpdateUserSchema` del backend no la
     * declara y Zod descarta lo no declarado, así que el cambio se perdía en silencio —
     * el resto del formulario se guardaba y la oficina quedaba igual. Va por su endpoint
     * dedicado, como el rol y el rank. (Admin Imperium, 18/09/2026)
     */
    updateUserOffice: builder.mutation<User, { id: string; officeId: string | null }>({
      query: ({ id, officeId }) => ({ url: `/users/${id}/office`, method: 'PUT', body: { officeId } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    /** Admin restringido (lib/restricciones.ts): oficinas excluidas y secciones ocultas. */
    updateUserRestricciones: builder.mutation<User, { id: string; oficinasExcluidas: string[]; seccionesOcultas: string[] }>({
      query: ({ id, ...body }) => ({ url: `/users/${id}/restricciones`, method: 'PUT', body }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    updateUserRank: builder.mutation<User, { id: string; agentRank: AgentRank }>({
      query: ({ id, agentRank }) => ({ url: `/users/${id}/rank`, method: 'PUT', body: { agentRank } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    updateUserStatus: builder.mutation<ResultadoBaja, { id: string; isActive: boolean; bajarPropiedades?: boolean }>({
      query: ({ id, isActive, bajarPropiedades }) => ({
        url: `/users/${id}/status`, method: 'PUT', body: { isActive, bajarPropiedades },
      }),
      transformResponse: (res: { data: ResultadoBaja }) => res.data,
      // Al bajar la cartera cambian las propiedades: sin invalidarlas, el listado sigue
      // mostrándolas activas hasta que alguien recargue.
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User', 'Property'],
    }),

    /** Qué se da de baja junto con el agente. Se pide al abrir la confirmación. */
    getPreviewBaja: builder.query<PreviewBaja, string>({
      query: (id) => `/users/${id}/preview-baja`,
      transformResponse: (res: { data: PreviewBaja }) => res.data,
    }),

    changePassword: builder.mutation<void, { id: string; currentPassword: string; newPassword: string }>({
      query: ({ id, currentPassword, newPassword }) => ({
        url: `/users/${id}/password`,
        method: 'PUT',
        body: { currentPassword, newPassword },
      }),
    }),

    // Reset de contraseña por admin: devuelve la clave temporal generada (una sola vez).
    adminResetPassword: builder.mutation<{ tempPassword: string }, { id: string }>({
      query: ({ id }) => ({ url: `/users/${id}/reset-password`, method: 'POST' }),
      transformResponse: (r: { data: { tempPassword: string } }) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }],
    }),

    refreshWaWhitelist: builder.mutation<{ data: { refreshed: boolean } }, void>({
      query: () => ({ url: '/users/wa-whitelist/refresh', method: 'POST' }),
    }),

    skipGustavoOnboarding: builder.mutation<void, void>({
      query: () => ({ url: '/users/me/gustavo-onboarding/skip', method: 'POST' }),
    }),

    /**
     * Seguimiento del agente: notas internas de los líderes.
     *
     * OJO, no confundir con `listMyMemories` de acá abajo: esas son las memorias que
     * GUSTAVO usa como contexto y las gestiona el propio agente. Estas son privadas de
     * los líderes y el agente no las ve nunca. (Admin Harmony, 14/08/2026)
     */
    listAgentNotes: builder.query<AgentNote[], string>({
      query: (id) => `/users/${id}/notes`,
      transformResponse: (res: { data: AgentNote[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'AgentNotes', id }],
    }),

    createAgentNote: builder.mutation<AgentNote, { id: string; content: string; kind?: 'note' | 'action'; dueAt?: string | null }>({
      query: ({ id, ...body }) => ({ url: `/users/${id}/notes`, method: 'POST', body }),
      transformResponse: (res: { data: AgentNote }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AgentNotes', id }],
    }),

    updateAgentNote: builder.mutation<AgentNote, { id: string; noteId: string; content: string; dueAt?: string | null }>({
      query: ({ id, noteId, ...body }) => ({ url: `/users/${id}/notes/${noteId}`, method: 'PUT', body }),
      transformResponse: (res: { data: AgentNote }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AgentNotes', id }],
    }),

    setAgentNoteDone: builder.mutation<AgentNote, { id: string; noteId: string; done: boolean }>({
      query: ({ id, noteId, done }) => ({ url: `/users/${id}/notes/${noteId}/done`, method: 'PUT', body: { done } }),
      transformResponse: (res: { data: AgentNote }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AgentNotes', id }],
    }),

    deleteAgentNote: builder.mutation<void, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({ url: `/users/${id}/notes/${noteId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AgentNotes', id }],
    }),

    // Memoria de largo plazo del agente (Sprint E)
    listMyMemories: builder.query<
      { count: number; memories: Array<{ key: string; value: string; lastReinforcedAt: string }> },
      void
    >({
      query: () => ({ url: '/users/me/memories' }),
      transformResponse: (res: { data: { count: number; memories: any[] } }) => res.data,
      providesTags: ['MyMemories'],
    }),

    createMyMemory: builder.mutation<{ key: string; value: string }, { value: string; key?: string }>({
      query: (body) => ({ url: '/users/me/memories', method: 'POST', body }),
      transformResponse: (res: { data: { key: string; value: string } }) => res.data,
      invalidatesTags: ['MyMemories'],
    }),

    improveMyMemory: builder.mutation<{ key: string; value: string }, string>({
      query: (text) => ({ url: '/users/me/memories/improve', method: 'POST', body: { text } }),
      transformResponse: (res: { data: { key: string; value: string } }) => res.data,
    }),

    deleteMyMemory: builder.mutation<void, string>({
      query: (key) => ({ url: `/users/me/memories/${encodeURIComponent(key)}`, method: 'DELETE' }),
      invalidatesTags: ['MyMemories'],
    }),

    // ─── Círculos de pertenencia (§6.3) ─────────────────────────────────
    getUserCircles: builder.query<CirclesOverview, string>({
      query: (id) => `/users/${id}/circles`,
      transformResponse: (res: { data: CirclesOverview }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'User', id: `${id}-circles` }],
    }),

    approveCircle: builder.mutation<unknown, { id: string; tier: CircleTier }>({
      query: ({ id, tier }) => ({ url: `/users/${id}/circles/${tier}/approve`, method: 'POST' }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id: `${id}-circles` }, { type: 'User', id }],
    }),

    evaluateCircles: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/users/${id}/circles/evaluate`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'User', id: `${id}-circles` }],
    }),

    // ─── Reconocimientos puntuales (§6.4) ───────────────────────────────
    getUserMilestones: builder.query<MilestoneItem[], string>({
      query: (id) => `/users/${id}/milestones`,
      transformResponse: (res: { data: MilestoneItem[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'User', id: `${id}-milestones` }],
    }),

    // ─── Calificaciones que dan los owners al agente ────────────────────
    getAgentRatingsSummary: builder.query<AgentRatingsSummary, string>({
      query: (id) => `/users/${id}/ratings/summary`,
      transformResponse: (res: { data: AgentRatingsSummary }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'AgentRating' as const, id: `agent-${id}` }],
    }),

    // ─── joinedAt (antigüedad editable por office_admin) ─────────────────
    updateJoinedAt: builder.mutation<User, { id: string; joinedAt: string }>({
      query: ({ id, joinedAt }) => ({ url: `/users/${id}/joined-at`, method: 'PUT', body: { joinedAt } }),
      transformResponse: (res: { data: User }) => res.data,
      // La LISTA también: sin esto la fila de atrás seguía con la fecha vieja después de
      // guardar, igual que el diálogo. `updateTag` ya la invalidaba; esta no. (24/08/2026)
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, { type: 'User', id: `${id}-circles` }, 'User'],
    }),

    // ─── Tag de acceso a la oficina (self o líder) ──────────────────────
    updateTag: builder.mutation<User, { id: string; tag: string | null }>({
      query: ({ id, tag }) => ({ url: `/users/${id}/tag`, method: 'PUT', body: { tag } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    // ─── Clasificación interna A/B/C/D (privada de líderes) ──────────────
    updateInternalGrade: builder.mutation<User, { id: string; grade: 'A' | 'B' | 'C' | 'D' | null }>({
      query: ({ id, grade }) => ({ url: `/users/${id}/internal-grade`, method: 'PUT', body: { grade } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    // ─── Termómetro comercial del agente frío/tibio/caliente (privado de líderes) ──
    updateAgentTemperature: builder.mutation<User, { id: string; temperature: 'cold' | 'warm' | 'hot' | null }>({
      query: ({ id, temperature }) => ({ url: `/users/${id}/temperature`, method: 'PUT', body: { temperature } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),

    // ─── Etiquetas de equipo ("alquileres", "ventas", …) — Admin Harmony ──────
    updateTeamTags: builder.mutation<User, { id: string; tags: string[] }>({
      query: ({ id, tags }) => ({ url: `/users/${id}/team-tags`, method: 'PUT', body: { tags } }),
      transformResponse: (res: { data: User }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User'],
    }),
    // Catálogo de etiquetas en uso (autocomplete + selects de filtro).
    getTeamTags: builder.query<string[], { office?: string } | void>({
      query: (args) => ({ url: '/users/team-tags', params: args && args.office ? { office: args.office } : undefined }),
      transformResponse: (res: { data: string[] }) => res.data,
      providesTags: ['User'],
    }),
    getBajasStats: builder.query<BajasStats, { period: 'week' | 'month' | 'year'; office?: string }>({
      query: (args) => ({ url: '/users/bajas-stats', params: { period: args.period, ...(args.office ? { office: args.office } : {}) } }),
      transformResponse: (res: { data: BajasStats }) => res.data,
      providesTags: ['User'],
    }),
  }),
});

export interface BajasStats {
  period: 'week' | 'month' | 'year';
  label: string;
  total: number;
  prevTotal: number;
  byTeam: { bucket: 'alquileres' | 'todo' | 'ventas' | null; label: string; count: number }[];
  bajas: BajaDetalle[];
  /** Cuántas de `bajas` no tienen fecha de alta: el panel lo declara en vez de disimularlo. */
  sinFechaDeAlta: number;
}

export interface BajaDetalle {
  id: string;
  name: string;
  avatar: string | null;
  office: string;
  team: 'alquileres' | 'todo' | 'ventas' | null;
  /** Fecha de baja. */
  date: string;
  joinedAt: string | null;
  /** Meses que estuvo en la empresa. `null` = no hay fecha de alta, va "sin dato". */
  antiguedadMeses: number | null;
}

// ─── Tipos compartidos con backend (circles.service / milestones.service) ─────

export type CircleTier   = 'amber' | 'gold' | 'nova' | 'imperial' | 'rhodium';
export type CircleStatus = 'not_eligible' | 'eligible' | 'pending_approval' | 'granted';

export interface CircleSummary {
  tier:           CircleTier;
  label:          string;
  symbol:         string;
  minLifetimeUSD: number;
  minTenureYears: number;
  status:         CircleStatus;
  eligibleAt?:    string;
  approvedAt?:    string;
  approvedBy?:    string;
}

export interface CirclesOverview {
  agentLifetimeUSD: number;
  agentTenureYears: number;
  circles:          CircleSummary[];
  nextTier: {
    tier:             CircleTier;
    label:            string;
    deltaLifetimeUSD: number;
    deltaTenureYears: number;
  } | null;
}

export interface AgentRatingsSummary {
  total: number;
  averageOverall: number | null;
  byDimension: {
    comunicacion:        number | null;
    conocimientoMercado: number | null;
    disponibilidad:      number | null;
    gestionVisitas:      number | null;
    profesionalismo:     number | null;
  };
  nps: number | null;
  recommendCount: number;
  recentComments: Array<{
    overall:        number;
    comment:        string;
    wouldRecommend: boolean;
    createdAt:      string;
    author:         string;
  }>;
  trend: {
    recentAvg:   number;
    previousAvg: number | null;
    delta:       number | null;
  } | null;
}

export interface MilestoneItem {
  key:            string;
  label:          string;
  minLifetimeUSD: number;
  awarded:        boolean;
  awardedAt?:     string;
}

export const {
  useGetMiPortfolioQuery,
  useUpdateMiPortfolioMutation,
  useGetMyProfileQuery,
  useMarkShowcaseSeenMutation,
  useMarkOnboardingCompleteMutation,
  useMarcarAvisoVistoMutation,
  useSetGustavoMorningBriefMutation,
  useGetMyWhatsappStatusQuery,
  useListUsersQuery,
  useListColleaguesQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUploadUserAvatarMutation,
  useUpdateUserRoleMutation,
  useUpdateUserOfficeMutation,
  useUpdateUserRestriccionesMutation,
  useUpdateUserRankMutation,
  useUpdateUserStatusMutation,
  useLazyGetPreviewBajaQuery,
  useChangePasswordMutation,
  useAdminResetPasswordMutation,
  useRefreshWaWhitelistMutation,
  useSkipGustavoOnboardingMutation,
  useListAgentNotesQuery,
  useCreateAgentNoteMutation,
  useUpdateAgentNoteMutation,
  useDeleteAgentNoteMutation,
  useSetAgentNoteDoneMutation,
  useListMyMemoriesQuery,
  useCreateMyMemoryMutation,
  useImproveMyMemoryMutation,
  useDeleteMyMemoryMutation,
  useGetUserCirclesQuery,
  useApproveCircleMutation,
  useEvaluateCirclesMutation,
  useGetUserMilestonesQuery,
  useGetAgentRatingsSummaryQuery,
  useUpdateJoinedAtMutation,
  useUpdateTagMutation,
  useUpdateInternalGradeMutation,
  useUpdateAgentTemperatureMutation,
  useUpdateTeamTagsMutation,
  useGetTeamTagsQuery,
  useGetBajasStatsQuery,
  useGetAltasStatsQuery,
  useGetInactividadStatsQuery,
} = usersApi;
